import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';

const Files = () => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);

    const fetchFiles = async () => {
        const { data, error } = await supabase
            .from('file_uploads')
            .select('*')
            .order('uploaded_at', { ascending: false });

        if (!error) setFiles(data);
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const filePath = `${Date.now()}-${file.name}`;

        const { error: uploadError } = await supabase.storage
            .from('files')
            .upload(filePath, file);

        if (uploadError) {
            alert('Upload failed');
            setUploading(false);
            return;
        }

        const { data: urlData } = supabase.storage
            .from('files')
            .getPublicUrl(filePath);

        const { error: insertError } = await supabase.from('file_uploads').insert({
            filename: file.name,
            url: urlData.publicUrl,
        });

        if (insertError) {
            alert('Insert failed');
        }

        setUploading(false);
        fetchFiles();
    };

    const handleDelete = async (file) => {
        const confirm = window.confirm(`Are you sure you want to delete "${file.filename}"?`);
        if (!confirm) return;

        const filePath = file.url.split('/').pop();

        // Delete from storage
        const { error: storageError } = await supabase.storage
            .from('files')
            .remove([filePath]);

        if (storageError) {
            alert('Failed to delete from storage');
            return;
        }

        // Delete from database
        const { error: dbError } = await supabase
            .from('file_uploads')
            .delete()
            .eq('id', file.id);

        if (dbError) {
            alert('Failed to delete from database');
            return;
        }

        fetchFiles();
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>📂 Files</Typography>

            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    disabled={uploading}
                >
                    Upload File
                    <input
                        type="file"
                        hidden
                        onChange={handleUpload}
                    />
                </Button>
                {uploading && <CircularProgress size={24} />}
            </Box>

            <Paper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>File</TableCell>
                            <TableCell>URL</TableCell>
                            <TableCell>Uploaded</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {files.map((file) => (
                            <TableRow key={file.id}>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <InsertDriveFileIcon color="action" />
                                        {file.filename}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <a href={file.url} target="_blank" rel="noreferrer" style={{ color: '#1976d2' }}>
                                        View
                                    </a>
                                </TableCell>
                                <TableCell>
                                    {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleDelete(file)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
};

export default Files;
